import { createRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { rootRoute } from './__root';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-keys';
import { PermissionGate } from '@/components/admin/permission-gate';
import { AdminSubNav } from '@/components/admin/admin-sub-nav';
import { CostHeadline } from '@/components/admin/costs/cost-headline';
import { CostLineTable } from '@/components/admin/costs/cost-line-table';
import { CostHistoryChart } from '@/components/admin/costs/cost-history-chart';

const HISTORY_MONTHS = 6;

/** Pull the message out of a contract `{ error: { code, message } }` envelope without unsafe casts. */
function extractApiErrorMessage(envelope: unknown, fallback: string): string {
  if (envelope && typeof envelope === 'object' && 'error' in envelope) {
    const inner = (envelope as { error?: unknown }).error;
    if (inner && typeof inner === 'object' && 'message' in inner) {
      const msg = (inner as { message?: unknown }).message;
      if (typeof msg === 'string') return msg;
    }
  }
  return `${fallback} request failed`;
}

export const adminObservabilityCostsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/observability/costs',
  component: AdminObservabilityCostsPage,
});

function AdminObservabilityCostsPage() {
  return (
    <PermissionGate permission="platform:observe">
      <Costs />
    </PermissionGate>
  );
}

function Costs() {
  const reportQuery = useQuery({
    queryKey: queryKeys.admin.costs(),
    refetchInterval: 60_000,
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/admin/observability/costs');
      if (error) throw new Error(extractApiErrorMessage(error, 'cost report'));
      return data;
    },
  });

  const historyQuery = useQuery({
    queryKey: queryKeys.admin.costHistory(HISTORY_MONTHS),
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await apiClient.GET(
        '/admin/observability/costs/history',
        { params: { query: { months: HISTORY_MONTHS } } }
      );
      if (error) throw new Error(extractApiErrorMessage(error, 'cost history'));
      return data;
    },
  });

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-2xl font-semibold">Monthly Costs</h1>
      <AdminSubNav active="costs" />

      <p className="text-xs text-muted-foreground">
        Projection from in-source pricing catalog × Cloudflare GraphQL Analytics
        usage. Not the Cloudflare invoice — see the Billable Usage dashboard
        for billable totals.
      </p>

      {reportQuery.isLoading && (
        <p className="text-sm text-muted-foreground">Loading cost report…</p>
      )}
      {reportQuery.isError && (
        <p className="text-sm text-red-600">Failed to load cost report.</p>
      )}
      {reportQuery.data && (
        <>
          <CostHeadline report={reportQuery.data} />
          <CostLineTable lines={reportQuery.data.lines} />
        </>
      )}

      {historyQuery.isLoading && (
        <p className="text-sm text-muted-foreground">Loading history…</p>
      )}
      {historyQuery.data && <CostHistoryChart history={historyQuery.data.items} />}
    </div>
  );
}
